import { all, call, put, takeLatest, takeLeading } from '@redux-saga/core/effects';
import axios, { AxiosResponse } from 'axios';

import { SagaPayload } from '../../api.types';
import {
  UpdateMemberBadgesRequest,
  UpdateMemberBadgesResponse,
  ViewMemberBadgesResponse,
  ViewmemberBadgesRequest
} from './member-badge.types';

import { RETRIEVING_MEMBER_BADGES, UPDATING_MEMBER_BADGES } from './member-badge.constants';

import { requestModifyMemberBadges, requestViewMemberBadges } from './member-badge.actions';

import { getUpdateMemberBadgesRequest, getViewMemberBadgesRequest } from './member-badge.endpoints';

import { setMemberBadges, updateMemberBadges } from '../../../slices/programme/member/member.slice';
import { addSpinner, addSystemNotice, removeSpinner } from '../../../slices/system/system.slice';

import { getErrorMessage } from '../../../utils/api.util';
import { ERROR, SUCCESS } from '../../../utils/constants';

export function* performViewMemberBadgesRequest({ payload }: SagaPayload<ViewmemberBadgesRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(RETRIEVING_MEMBER_BADGES));

    const [endpoint, options] = getViewMemberBadgesRequest(requestData);

    const { data }: AxiosResponse<ViewMemberBadgesResponse> = yield call(axios, endpoint, options);

    yield put(setMemberBadges(data.memberBadges));

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(RETRIEVING_MEMBER_BADGES));
  }
}

export function* watchForViewMemberBadgesRequest() {
  yield takeLeading(requestViewMemberBadges.type, performViewMemberBadgesRequest);
}

export function* performUpdateMemberBadgesRequest({ payload }: SagaPayload<UpdateMemberBadgesRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(UPDATING_MEMBER_BADGES));

    const [endpoint, options] = getUpdateMemberBadgesRequest(requestData);

    const { data }: AxiosResponse<UpdateMemberBadgesResponse> = yield call(axios, endpoint, options);

    const badges = data.memberBadges.filter((badge) => badge.badgeCount);

    yield put(updateMemberBadges({ memberExternalReference: requestData.memberExternalReference, badges }));

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(UPDATING_MEMBER_BADGES));
  }
}

export function* watchForUpdateMemberBadgesRequest() {
  yield takeLatest(requestModifyMemberBadges.type, performUpdateMemberBadgesRequest);
}

export default function* memberBadgeSaga() {
  yield all([watchForViewMemberBadgesRequest(), watchForUpdateMemberBadgesRequest()]);
}
